import { useCallback } from "react";
import { Scale } from "lucide-react";
import { getUpcMetrics } from "@/api/client";
import type { UpcMetrics } from "@/api/types";
import { Button } from "@/components/ui/button";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { MeterCard } from "@/components/MeterCard";
import { StatCard } from "@/components/StatCard";
import { TwoSeriesTimeChart } from "@/components/TwoSeriesTimeChart";
import { UpcListInput } from "@/components/UpcListInput";
import { ChartSkeleton, ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useDebouncedUrlFilter, useUrlListFilter } from "@/hooks/useUrlFilters";
import { formatCompact, formatCurrency, formatNumber } from "@/lib/format";

// The Coca-Cola vs competitor sets from the Looker dashboard, for a
// one-click demo of the page.
const SAMPLE_MINE = ["0004900000044", "0004900005010", "0004900000634"];
const SAMPLE_COMPETITOR = ["0001200080994", "0001200000230", "0001200000129"];

type StoreCompareRow = { storeName: string; mine: number; competitor: number };

const storeCompareColumns: Column<StoreCompareRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  {
    header: "My UPC units",
    align: "right",
    cell: (r) => formatNumber(r.mine),
    sortValue: (r) => r.mine,
  },
  {
    header: "Competitor units",
    align: "right",
    cell: (r) => formatNumber(r.competitor),
    sortValue: (r) => r.competitor,
  },
];

type StoreUpcRow = UpcMetrics["byStoreUpc"][number];

const storeUpcColumns: Column<StoreUpcRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  { header: "UPC", cell: (r) => r.upc, sortValue: (r) => r.upc },
  {
    header: "Lowest price",
    align: "right",
    cell: (r) => formatCurrency(r.minPrice),
    sortValue: (r) => r.minPrice,
  },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

/** Union of both sides' daily units as {day, a, b} chart rows, zero-filled. */
function pairByDay(
  mine: UpcMetrics | null,
  competitor: UpcMetrics | null,
): { day: string; a: number; b: number }[] {
  const days = new Map<string, { a: number; b: number }>();
  for (const d of mine?.unitsByDay ?? []) {
    days.set(d.day, { a: d.units, b: 0 });
  }
  for (const d of competitor?.unitsByDay ?? []) {
    const entry = days.get(d.day) ?? { a: 0, b: 0 };
    entry.b = d.units;
    days.set(d.day, entry);
  }
  return [...days.entries()]
    .map(([day, v]) => ({ day, ...v }))
    .sort((x, y) => x.day.localeCompare(y.day));
}

function totalUnits(side: UpcMetrics | null): number {
  return (side?.unitsByDay ?? []).reduce((s, d) => s + d.units, 0);
}

export function UpcComparePage() {
  const filters = useProductFilterOptions();
  const [mineDraft, setMineUpcs, mineUpcs] = useDebouncedUrlFilter("upcs");
  const [competitorDraft, setCompetitorUpcs, competitorUpcs] =
    useDebouncedUrlFilter("competitorUpcs");
  const [storeIds, setStoreIds, store] = useUrlListFilter("store");

  const fetchMine = useCallback(
    (token: string) =>
      getUpcMetrics(token, { upcs: mineUpcs || undefined, storeId: store }),
    [mineUpcs, store],
  );
  const fetchCompetitor = useCallback(
    (token: string) =>
      getUpcMetrics(token, {
        upcs: competitorUpcs || undefined,
        storeId: store,
      }),
    [competitorUpcs, store],
  );
  const mineState = useApiData(fetchMine);
  const competitorState = useApiData(fetchCompetitor);

  const loading =
    mineState.status === "loading" || competitorState.status === "loading";
  const error =
    mineState.status === "error"
      ? mineState
      : competitorState.status === "error"
        ? competitorState
        : null;
  const mine = mineState.status === "ready" ? mineState.data : null;
  const competitor =
    competitorState.status === "ready" ? competitorState.data : null;

  const mineTotal = totalUnits(mine);
  const competitorTotal = totalUnits(competitor);
  const combined = mineTotal + competitorTotal;

  const unitsData = pairByDay(mine, competitor);

  const storeRows = new Map<string, StoreCompareRow>();
  for (const s of mine?.byStore ?? []) {
    storeRows.set(s.storeName, {
      storeName: s.storeName,
      mine: s.units,
      competitor: 0,
    });
  }
  for (const s of competitor?.byStore ?? []) {
    const entry = storeRows.get(s.storeName) ?? {
      storeName: s.storeName,
      mine: 0,
      competitor: 0,
    };
    entry.competitor = s.units;
    storeRows.set(s.storeName, entry);
  }
  const storeCompareRows = [...storeRows.values()].sort(
    (a, b) => b.mine - a.mine,
  );

  return (
    <div className="flex flex-col gap-6" aria-busy={loading}>
      <div>
        <h1 className="text-xl font-semibold">UPC comparison</h1>
        <p className="text-sm text-muted-foreground">
          Your UPCs against a competitor's set over the last 30 days — units,
          share, and where each is winning.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <UpcListInput label="My UPCs" value={mineDraft} onApply={setMineUpcs} />
        <UpcListInput
          label="Competitor UPCs"
          value={competitorDraft}
          onApply={setCompetitorUpcs}
        />
        <MultiFilterSelect
          label="Store"
          allLabel="All stores"
          values={storeIds}
          onChange={setStoreIds}
          disabled={filters.disabled}
          options={filters.storeOptions}
        />
      </div>

      {error ? (
        <ErrorState message={error.message} onRetry={error.reload} />
      ) : !mineUpcs ? (
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed px-6 py-14 text-center">
          <div
            className="mb-1 flex h-11 w-11 items-center justify-center rounded-full"
            style={{ background: "var(--viz-meter-track)" }}
          >
            <Scale
              className="h-5 w-5"
              style={{ color: "var(--viz-series-1)" }}
              aria-hidden
            />
          </div>
          <p className="text-sm font-medium">Nothing to compare yet</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Add your UPCs (and optionally a competitor's) above — or start
            from a sample matchup.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              setMineUpcs(SAMPLE_MINE.join(","));
              setCompetitorUpcs(SAMPLE_COMPETITOR.join(","));
            }}
          >
            Try sample comparison
          </Button>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="My UPC units"
              value={mine ? formatCompact(mineTotal) : null}
            />
            <StatCard
              label="Competitor units"
              value={
                competitorUpcs
                  ? competitor
                    ? formatCompact(competitorTotal)
                    : null
                  : "—"
              }
            />
            <MeterCard
              label="My market share"
              value={
                mine && (competitor || !competitorUpcs)
                  ? combined > 0
                    ? mineTotal / combined
                    : null
                  : undefined
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Units over time</CardTitle>
              <CardDescription>Units sold per day, both sets</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <ChartSkeleton height={260} />
              ) : (
                <TwoSeriesTimeChart
                  data={unitsData}
                  aName="My UPCs"
                  bName="Competitor UPCs"
                  height={260}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
              <CardTitle>Units by store</CardTitle>
              <CardDescription>
                Units sold by store for each set
              </CardDescription>
              </div>
              {!loading ? (
                <CsvDownloadButton
                  name="upc-compare-by-store"
                  columns={storeCompareColumns}
                  rows={storeCompareRows}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {loading ? (
                <TableSkeleton />
              ) : (
                <DataTable
                  columns={storeCompareColumns}
                  rows={storeCompareRows}
                  rowKey={(r) => r.storeName}
                  maxHeight="28rem"
                  emptyMessage="No sales in range"
                />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1.5">
                  <CardTitle>My UPCs by store</CardTitle>
                  <CardDescription>
                    Lowest observed price per store per UPC
                  </CardDescription>
                </div>
                {mine ? (
                  <CsvDownloadButton
                    name="upc-compare-mine"
                    columns={storeUpcColumns}
                    rows={mine.byStoreUpc}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <TableSkeleton />
                ) : mine ? (
                  <DataTable
                    columns={storeUpcColumns}
                    rows={mine.byStoreUpc}
                    rowKey={(r) => `${r.storeName}-${r.upc}`}
                    maxHeight="28rem"
                    emptyMessage="No sales in range"
                  />
                ) : null}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1.5">
                  <CardTitle>Competitor UPCs by store</CardTitle>
                  <CardDescription>
                    Lowest observed price per store per UPC
                  </CardDescription>
                </div>
                {competitor ? (
                  <CsvDownloadButton
                    name="upc-compare-competitor"
                    columns={storeUpcColumns}
                    rows={competitor.byStoreUpc}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <TableSkeleton />
                ) : competitor ? (
                  <DataTable
                    columns={storeUpcColumns}
                    rows={competitor.byStoreUpc}
                    rowKey={(r) => `${r.storeName}-${r.upc}`}
                    maxHeight="28rem"
                    emptyMessage="No sales in range"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Add competitor UPCs to compare.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
